import WizardCheckbox from 'components/v2/epd-wizard-inputs/WizardCheckbox';
import WizardTextInputComponent from 'components/v2/epd-wizard-inputs/WizardTextInput';
import WizardTextInputControlledComponent from 'components/v2/epd-wizard-inputs/WizardTextInputControlled';
import SelectDataField from 'components/v2/form/select-data-field/SelectDataField';
import { HelpBox } from 'components/v2/help-boxes';
import { EPDModel, VerifierType } from 'services/EpdClient';
import { CheckBoxPanel, FieldContainer, FieldPanelInput } from 'styles/v2/Styles.styled';
import { Option } from 'types/types';
import EditDialog from './EditDialog';
import { ISO_14025_ID } from 'components/v2/compiler/constants';
import { useProductCategories } from 'services/api/standardEpdQueries';

type TProps = {
  isOpened: boolean;
  onHide: any;
  epd?: EPDModel;
  onChangeEpd: any;
  title: string | undefined;
  setTitle: any;
  conformityStandardsOptions?: Option[];
};

const EditDefinitionDialog = ({
  isOpened,
  onHide,
  epd,
  onChangeEpd,
  title,
  setTitle,
  conformityStandardsOptions,
}: TProps) => {
  const { statementOfConformityWithStandards } = epd || {};

  const productCategories = useProductCategories().data;

  function getSelectedStandards(
    statementOfConformityWithStandards: string | undefined,
    conformityStandardsOptions: Option[] | undefined
  ): Option[] | undefined {
    if (!statementOfConformityWithStandards) return [conformityStandardsOptions?.find((y) => y.value === ISO_14025_ID)!];

    const standardsArray = statementOfConformityWithStandards?.toString().split(',');

    return standardsArray?.map((x) => conformityStandardsOptions?.find((y) => y.value === x)!);
  }

  const accreditedCertificationBodyAddress = epd?.epdVerifier?.companyAddress
    ? [
        epd.epdVerifier.companyAddress.country,
        epd.epdVerifier.companyAddress.city,
        epd.epdVerifier.companyAddress.zipCode,
        epd.epdVerifier.companyAddress.addressLine,
      ]
        .filter((value: string | null | undefined) => !!value && value.trim() !== '')
        .join(', ')
    : '';

  return (
    <EditDialog header="EPD Definition" isOpened={isOpened} onHide={onHide}>
      <HelpBox>
        Please fill-in the required information below. The filled data will be auto saved and available in the table.
      </HelpBox>
      <FieldPanelInput>
        <FieldContainer>
          <WizardTextInputComponent
            label="UUID of data set"
            name="uuid"
            tooltip="Automatically generated universally unique identifier of this data set."
            disabled
            required
            value={epd?.id}
            onChanged={onChangeEpd}
          />
        </FieldContainer>
        <FieldContainer>
          <WizardTextInputControlledComponent
            label="EPD name"
            tooltip="General descriptive and specifying name of the product/system."
            tooltipPosition={'right'}
            required
            value={title}
            onChange={(e: any) => setTitle(e.target.value)}
            onChanged={onChangeEpd}
            name="title"
            maxLength={150}
          />
        </FieldContainer>
        <FieldContainer>
          <SelectDataField
            label="LCA Practitioner"
            isMulti
            tooltip="The person accountable for the underlying Life Cycle Assessment."
            name="practitionersIds"
            value={epd?.epdDevelopers?.map((x) => {
              return { value: x.contactUserId, label: x.contactName };
            })}
            options={
              epd?.epdDevelopers?.map((x) => {
                return { value: x.contactUserId!, label: x.contactName! };
              }) ?? []
            }
            disabled
            components={{ DropdownIndicator: null }}
          />
        </FieldContainer>
        <FieldContainer>
          <SelectDataField
            label="Epd verifier"
            tooltip="Individual responsible for validating or confirming various aspects of the product lifecycle."
            tooltipPosition="right"
            name="verifierId"
            value={{ value: epd?.epdVerifier?.id, label: epd?.epdVerifier?.displayName }}
            options={[{ value: epd?.epdVerifier?.id!, label: epd?.epdVerifier?.displayName! }]}
            disabled
            components={{ DropdownIndicator: null }}
          />
        </FieldContainer>
        {epd?.epdVerifier?.verifierType === VerifierType.Body && (
          <FieldContainer>
            <WizardTextInputComponent
              label="Accredited certification body address"
              name="bodyAddress"
              value={accreditedCertificationBodyAddress}
              disabled
            />
          </FieldContainer>
        )}
        <FieldContainer>
          <WizardTextInputComponent
            label="Product category"
            required
            name="productCategoryId"
            value={productCategories?.find((c) => c.id === epd?.productCategoryId)?.title}
            tooltip="Product Category"
            tooltipPosition="right"
            disabled
          />
        </FieldContainer>
        <FieldContainer>
          <WizardTextInputComponent
            label="PCR"
            required
            name="pcr"
            value={epd?.pcr?.fullName}
            tooltip="Product Category Rules"
            tooltipPosition="right"
            disabled
          />
        </FieldContainer>
        <FieldContainer>
          <WizardTextInputComponent
            label="C-PCR"
            name="cpcr"
            value={epd?.cpcr?.fullName}
            tooltip="Complementary Product Category Rules"
            tooltipPosition="right"
            disabled
          />
        </FieldContainer>
        <FieldContainer>
          <SelectDataField
            label="Standards conformance"
            isMulti
            tooltip="Select the standards with which the EPD is compliant."
            name="statementOfConformityWithStandards"
            value={getSelectedStandards(statementOfConformityWithStandards, conformityStandardsOptions)}
            options={getSelectedStandards(statementOfConformityWithStandards, conformityStandardsOptions) ?? []}
            disabled
            components={{ DropdownIndicator: null }}
          />
        </FieldContainer>
        <FieldContainer>
          <CheckBoxPanel>
            <WizardCheckbox
              label={'Sector EPD'}
              name={'isSectorEpd'}
              value={epd?.isSectorEPD as boolean}
              onChanged={onChangeEpd}
              tooltip="The International EPD® System allows for an industry association to develop an EPD in the form of a sector EPD. A sector EPD declares the average product of multiple companies in a clearly defined sector and/or geographical area. Any communication of the results from a sector EPD should contain the information that the results are based on averages obtained from the sector as defined in the EPD. The communication shall not claim that the sector EPD results are representative for a certain manufacturer or its product."
              tooltipPosition="right"
            />
          </CheckBoxPanel>
        </FieldContainer>
      </FieldPanelInput>
    </EditDialog>
  );
};
export default EditDefinitionDialog;
